import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Target,
  Plus,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  PieChart,
  BarChart3,
  AlertCircle,
  CheckCircle,
  Edit,
  Trash2,
  Coffee,
  Car,
  Home,
  ShoppingBag,
  Gamepad2,
  Plane,
  Heart,
  BookOpen,
  Smartphone,
  X,
  ChevronRight,
  RefreshCw,
  Loader2,
  Check
} from 'lucide-react'
import { BudgetsAPI, TransactionsAPI, extractArray } from '../services/api'

const categoryIcons = {
  food: { icon: Coffee, color: 'bg-orange-50 text-orange-600' },
  transport: { icon: Car, color: 'bg-blue-50 text-blue-600' },
  housing: { icon: Home, color: 'bg-green-50 text-green-600' },
  shopping: { icon: ShoppingBag, color: 'bg-pink-50 text-pink-600' },
  entertainment: { icon: Gamepad2, color: 'bg-purple-50 text-purple-600' },
  travel: { icon: Plane, color: 'bg-cyan-50 text-cyan-600' },
  healthcare: { icon: Heart, color: 'bg-red-50 text-red-600' },
  education: { icon: BookOpen, color: 'bg-indigo-50 text-indigo-600' },
  utilities: { icon: Smartphone, color: 'bg-slate-100 text-slate-600' }
}

export default function BudgetManager() {
  const [budgets, setBudgets] = useState([])
  const [transactions, setTransactions] = useState([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [viewMode, setViewMode] = useState('grid')
  const [selectedPeriod, setSelectedPeriod] = useState('monthly')

  // UI states
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState('')

  // Form state
  const [newBudget, setNewBudget] = useState({
    category: '',
    name: '',
    amount: '',
    period: 'monthly'
  })
  const [editingBudget, setEditingBudget] = useState(null)

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [budgetsRes, transactionsRes] = await Promise.all([
        BudgetsAPI.getAll({ period: selectedPeriod }),
        TransactionsAPI.getAll({ limit: 10 })
      ])

      if (budgetsRes.success) {
        const rawBudgets = extractArray(budgetsRes)
        const budgetData = rawBudgets.map(b => ({
          id: b.id,
          category: b.category || 'other',
          name: b.name || b.category,
          budget: parseFloat(b.amount || 0),
          spent: parseFloat(b.spent || 0),
          period: b.period || 'monthly',
          transactions: b.transaction_count || 0,
          trend: b.trend || 'stable',
          lastTransaction: b.last_transaction_date ? new Date(b.last_transaction_date).toLocaleDateString() : 'N/A'
        }))
        setBudgets(budgetData)
      } else {
        setBudgets([])
      }

      if (transactionsRes.success) {
        const rawTx = extractArray(transactionsRes, 'transactions')
        const txData = rawTx.map(t => ({
          id: t.id,
          category: t.category || 'other',
          merchant: t.merchant || t.description || 'Transaction',
          amount: parseFloat(t.amount || 0),
          date: t.created_at ? new Date(t.created_at).toLocaleDateString() : 'N/A'
        }))
        setTransactions(txData)
      } else {
        setTransactions([])
      }
    } catch (err) {
      console.error('Error fetching budget data:', err)
      setError('Failed to load budget data')
      setBudgets([])
      setTransactions([])
    } finally {
      setLoading(false)
    }
  }, [selectedPeriod])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const showSuccess = (message) => {
    setSuccessMessage(message)
    setTimeout(() => setSuccessMessage(''), 3000)
  }

  // Create budget
  const handleCreateBudget = async () => {
    if (!newBudget.category || !newBudget.amount) {
      setError('Please fill in all required fields')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const res = await BudgetsAPI.create({
        category: newBudget.category,
        name: newBudget.name || categoryIcons[newBudget.category]?.name || newBudget.category,
        amount: parseFloat(newBudget.amount),
        period: newBudget.period
      })
      if (res.success) {
        setShowCreateModal(false)
        setNewBudget({ category: '', name: '', amount: '', period: 'monthly' })
        showSuccess('Budget created successfully')
        fetchData()
      } else {
        setError(res.message || 'Failed to create budget')
      }
    } catch (err) {
      setError(err.message || 'Failed to create budget')
    } finally {
      setSaving(false)
    }
  }

  // Update budget
  const handleUpdateBudget = async () => {
    if (!editingBudget) return
    setSaving(true)
    setError(null)
    try {
      const res = await BudgetsAPI.update(editingBudget.id, {
        amount: parseFloat(editingBudget.budget),
        name: editingBudget.name
      })
      if (res.success) {
        setEditingBudget(null)
        showSuccess('Budget updated successfully')
        fetchData()
      } else {
        setError(res.message || 'Failed to update budget')
      }
    } catch (err) {
      setError(err.message || 'Failed to update budget')
    } finally {
      setSaving(false)
    }
  }

  // Delete budget
  const handleDeleteBudget = async (id) => {
    if (!confirm('Are you sure you want to delete this budget?')) return
    try {
      const res = await BudgetsAPI.delete(id)
      if (res.success) {
        setBudgets(prev => prev.filter(b => b.id !== id))
        showSuccess('Budget deleted')
      } else {
        setError(res.message || 'Failed to delete budget')
      }
    } catch (err) {
      setError(err.message || 'Failed to delete budget')
    }
  }

  const totalBudget = budgets.reduce((sum, budget) => sum + budget.budget, 0)
  const totalSpent = budgets.reduce((sum, budget) => sum + budget.spent, 0)
  const totalRemaining = totalBudget - totalSpent

  const getStatusColor = (budget, spent) => {
    const percentage = (spent / budget) * 100
    if (percentage >= 100) return 'text-red-600'
    if (percentage >= 80) return 'text-orange-600'
    if (percentage >= 60) return 'text-yellow-600'
    return 'text-emerald-600'
  }

  const getProgressColor = (budget, spent) => {
    const percentage = (spent / budget) * 100
    if (percentage >= 100) return 'bg-red-600'
    if (percentage >= 80) return 'bg-orange-600'
    if (percentage >= 60) return 'bg-yellow-600'
    return 'bg-emerald-600'
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500">Loading budgets...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Budget Manager</h1>
              <p className="text-sm text-slate-500 mt-0.5">Track and manage your spending goals</p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={fetchData}
                className="p-2 text-slate-500 hover:text-slate-900 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>

              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-slate-900 transition-colors"
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>

              <div className="flex bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'grid' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'
                  }`}
                >
                  <PieChart className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'list' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                </button>
              </div>

              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Budget
              </button>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2"
            >
              <Check className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-700">{successMessage}</span>
            </motion.div>
          )}

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2"
            >
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-700">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">&times;</button>
            </motion.div>
          )}
        </div>
      </div>

      <div className="p-8 max-w-[1600px] mx-auto">
        {/* Overview Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-slate-500 font-medium">Total Budget</p>
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-blue-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">${totalBudget.toLocaleString()}</p>
            <p className="text-xs text-slate-500 mt-1">This {selectedPeriod.slice(0, -2)}</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-slate-500 font-medium">Total Spent</p>
              <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-orange-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">${totalSpent.toLocaleString()}</p>
            <p className="text-xs text-slate-500 mt-1">
              {((totalSpent / totalBudget) * 100).toFixed(1)}% of budget
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-slate-500 font-medium">Remaining</p>
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Target className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-emerald-600">${totalRemaining.toLocaleString()}</p>
            <p className="text-xs text-slate-500 mt-1">
              {((totalRemaining / totalBudget) * 100).toFixed(1)}% left
            </p>
          </div>
        </div>

        {/* Budget Cards */}
        <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8' : 'space-y-4 mb-8'}>
          {budgets.map((budget, index) => {
            const CategoryIcon = categoryIcons[budget.category].icon
            const percentage = (budget.spent / budget.budget) * 100
            const isOverBudget = percentage >= 100

            return (
              <motion.div
                key={budget.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white border border-slate-200 rounded-xl p-5 hover:border-slate-300 transition-all"
              >
                {/* Category Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${categoryIcons[budget.category].color}`}>
                      <CategoryIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">{budget.name}</h3>
                      <p className="text-xs text-slate-500">{budget.transactions} transactions</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setEditingBudget(budget)}
                      className="p-1.5 text-slate-400 hover:text-slate-900 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteBudget(budget.id)}
                      className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Budget Progress */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-900">
                      ${budget.spent.toLocaleString()} / ${budget.budget.toLocaleString()}
                    </span>
                    <span className={`text-sm font-bold ${getStatusColor(budget.budget, budget.spent)}`}>
                      {percentage.toFixed(1)}%
                    </span>
                  </div>

                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(percentage, 100)}%` }}
                      transition={{ duration: 1, delay: index * 0.2 }}
                      className={`h-2 rounded-full ${getProgressColor(budget.budget, budget.spent)}`}
                    />
                  </div>

                  {isOverBudget && (
                    <div className="flex items-center mt-2 text-red-600 text-xs">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      ${(budget.spent - budget.budget).toLocaleString()} over budget
                    </div>
                  )}
                </div>

                {/* Additional Info */}
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Last: {budget.lastTransaction}</span>
                  <div className="flex items-center gap-1">
                    {budget.trend === 'up' && <TrendingUp className="w-3 h-3 text-red-600" />}
                    {budget.trend === 'down' && <TrendingDown className="w-3 h-3 text-emerald-600" />}
                    <span className="capitalize">{budget.trend}</span>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* Recent Transactions */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900">Recent Budget Transactions</h3>
          </div>

          <div className="divide-y divide-slate-200">
            {transactions.map((transaction, index) => {
              const catInfo = categoryIcons[transaction.category] || categoryIcons.utilities
              const CategoryIcon = catInfo.icon

              return (
                <motion.div
                  key={transaction.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="px-6 py-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${catInfo.color}`}>
                        <CategoryIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900">{transaction.merchant}</h4>
                        <p className="text-xs text-slate-500">{transaction.date}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-900">-${transaction.amount.toFixed(2)}</p>
                        <p className="text-xs text-slate-500 capitalize">{transaction.category}</p>
                      </div>
                      <button className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Create Budget Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md border border-slate-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-900">Create New Budget</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-slate-700 text-sm font-medium mb-2">Category *</label>
                  <select
                    value={newBudget.category}
                    onChange={(e) => setNewBudget(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-slate-900 transition-colors"
                  >
                    <option value="">Select Category</option>
                    {Object.keys(categoryIcons).map(category => (
                      <option key={category} value={category} className="capitalize">
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-slate-700 text-sm font-medium mb-2">Budget Name</label>
                  <input
                    type="text"
                    value={newBudget.name}
                    onChange={(e) => setNewBudget(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Food & Dining"
                    className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-900 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-slate-700 text-sm font-medium mb-2">Budget Amount *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-500">$</span>
                    <input
                      type="number"
                      value={newBudget.amount}
                      onChange={(e) => setNewBudget(prev => ({ ...prev, amount: e.target.value }))}
                      placeholder="500"
                      className="w-full pl-8 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-900 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-700 text-sm font-medium mb-2">Period</label>
                  <select
                    value={newBudget.period}
                    onChange={(e) => setNewBudget(prev => ({ ...prev, period: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-slate-900 transition-colors"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateBudget}
                  disabled={saving || !newBudget.category || !newBudget.amount}
                  className="flex-1 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Create Budget
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Budget Modal */}
      <AnimatePresence>
        {editingBudget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setEditingBudget(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md border border-slate-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-900">Edit Budget</h3>
                <button
                  onClick={() => setEditingBudget(null)}
                  className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-slate-700 text-sm font-medium mb-2">Budget Name</label>
                  <input
                    type="text"
                    value={editingBudget.name}
                    onChange={(e) => setEditingBudget(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-slate-900 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-slate-700 text-sm font-medium mb-2">Budget Amount</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-500">$</span>
                    <input
                      type="number"
                      value={editingBudget.budget}
                      onChange={(e) => setEditingBudget(prev => ({ ...prev, budget: e.target.value }))}
                      className="w-full pl-8 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-slate-900 transition-colors"
                    />
                  </div>
                </div>

                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">
                    Current spending: <span className="font-semibold text-slate-900">${editingBudget.spent?.toLocaleString() || 0}</span>
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setEditingBudget(null)}
                  className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateBudget}
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save Changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
